/**
 * LEGAL-13 — Tab „Kosten": current-state cost of active subscriptions.
 *
 * Breakdown by binding (scope), with one-time vs. annual cost, by-role and
 * by-site rollups, and a per-cluster drill-down (site × role × period). This
 * is monitoring, not decision support — it consumes the same engine as the
 * subscription preview via the cost-breakdown endpoint.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

import Section from "../../components/Section";
import Metric from "../../components/Metric";
import { useModalContext } from "../../components/ModalProvider";
import { useCustomerTheme } from "../../Theme";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { qualificationRoleName, regulatedObjectName } from "../../administration/operator-responsibility/legalDisplay";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  sites: any[];
  refreshSignal: number;
}

const EMPTY_COST_BREAKDOWN = {
  currency: "EUR",
  totals: { oneTimeCost: 0, annualCost: 0 },
  bindings: [],
};

const EMPTY_BINDING_ESTIMATE = {
  oneTimeCost: 0,
  annualCost: 0,
  inspectionsPerYear: 0,
  assetsMatched: 0,
  sitesCovered: 0,
  byRole: [],
  clusters: [],
  flags: {},
};

const costValue = (value: any) => (typeof value === "number" && Number.isFinite(value) ? value : 0);

const sumBindingEstimateCosts = (bindings: any[], field: "oneTimeCost" | "annualCost") =>
  bindings.reduce((total, binding) => total + costValue(binding?.estimate?.[field]), 0);

const normalizeBindingEstimate = (estimate: any) => ({
  ...EMPTY_BINDING_ESTIMATE,
  ...(estimate || {}),
  oneTimeCost: costValue(estimate?.oneTimeCost),
  annualCost: costValue(estimate?.annualCost),
  inspectionsPerYear: costValue(estimate?.inspectionsPerYear),
  assetsMatched: costValue(estimate?.assetsMatched),
  sitesCovered: costValue(estimate?.sitesCovered),
  byRole: Array.isArray(estimate?.byRole) ? estimate.byRole : EMPTY_BINDING_ESTIMATE.byRole,
  clusters: Array.isArray(estimate?.clusters) ? estimate.clusters : EMPTY_BINDING_ESTIMATE.clusters,
  flags: estimate?.flags || EMPTY_BINDING_ESTIMATE.flags,
});

const normalizeCostBreakdown = (result: any) => {
  const bindings = Array.isArray(result?.bindings)
    ? result.bindings.map((binding: any) => ({
        ...binding,
        estimate: normalizeBindingEstimate(binding?.estimate),
      }))
    : EMPTY_COST_BREAKDOWN.bindings;
  const totals = result?.totals || {};

  return {
    ...EMPTY_COST_BREAKDOWN,
    ...(result || {}),
    currency: result?.currency || EMPTY_COST_BREAKDOWN.currency,
    totals: {
      oneTimeCost: totals.oneTimeCost ?? sumBindingEstimateCosts(bindings, "oneTimeCost"),
      annualCost: totals.annualCost ?? sumBindingEstimateCosts(bindings, "annualCost"),
    },
    bindings,
  };
};

export default function CostBreakdownPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const { theme } = useCustomerTheme();
  const [authentication] = useAtom(authenticationAtom) as any;
  const legalAPI = useLegalBindingAPI(authentication?.customer);

  const [data, setData] = useState<any>(null);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!authentication?.customer) {
      setData(null);
      setLoadError(false);
      return;
    }
    setData(null);
    setLoadError(false);
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getCostBreakdown();
        if (!cancelled) {
          setData(normalizeCostBreakdown(result));
          setLoadError(false);
        }
      } catch (error) {
        if (!cancelled) {
          pushError(error);
          setLoadError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, properties.refreshSignal]);

  const formatEUR = (value: number) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: data?.currency || "EUR" }).format(value || 0);

  const siteName = (siteId: string | null) => {
    if (!siteId) return "—";
    const site = (properties.sites || []).find((s: any) => String(s._id) === String(siteId));
    return site ? site.name || `${site.street} ${site.streetNumber}` : siteId;
  };

  const scopeSummary = (binding: any) => {
    const anchor =
      binding.scope?.anchor?.type === "Site"
        ? siteName(binding.scope.anchor.siteId)
        : t("operatorObligationsPanel.portfolio");
    const objects =
      binding.scope?.regulatedObjects === "ALL"
        ? t("operatorObligationsPanel.allRegulatedObjects")
        : (binding.scope?.regulatedObjects || [])
            .map((code: string) => regulatedObjectName(t, code))
            .join(", ");
    return `${anchor} × ${objects}`;
  };

  if (loadError) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <Alert variant="destructive">
          <AlertTriangle aria-hidden className="h-4 w-4" />
          <AlertDescription>{t("operatorObligationsPanel.costBreakdownLoadFailed")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4"
        role="status"
        aria-live="polite"
      >
        {/* Der Ladehinweis bleibt für Screenreader hörbar, sichtbar tragen die
            Platzhalter den Status. */}
        <span className="sr-only">{t("global.loading")}</span>
        <div className="flex flex-wrap gap-4">
          <Skeleton className="h-24 w-52" />
          <Skeleton className="h-24 w-52" />
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if ((data.bindings || []).length === 0) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-2 rounded-lg border border-dashed p-6 text-center">
        <p className="text-sm font-semibold text-foreground">
          {t("operatorObligationsPanel.noBindingsForCost")}
        </p>
        <p className="mx-auto max-w-prose text-sm text-muted-foreground">
          {t("operatorObligationsPanel.noBindingsForCostHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {/* Tenant totals */}
      <div className="flex flex-wrap gap-4">
        <Metric
          title={t("operatorObligationsPanel.totalOneTime")}
          value={formatEUR(data.totals.oneTimeCost)}
          color={theme.tertiary}
          size="large"
        />
        <Metric
          title={t("operatorObligationsPanel.totalAnnual")}
          value={formatEUR(data.totals.annualCost)}
          color={theme.secondary}
          size="large"
        />
      </div>
      <p className="text-xs text-warning">{t("operatorObligationsPanel.costEstimateDisclaimer")}</p>

      {(data.bindings || []).map((item: any) => {
        const key = String(item.bindingId);
        const isOpen = !!expanded[key];
        const estimate = item.estimate;
        return (
          <Section
            key={key}
            title={`${scopeSummary(item)} · ${t(`operatorObligationsPanel.conformance.${item.conformanceClass}`)}`}
          >
            <div className="flex min-w-0 flex-wrap gap-x-6 gap-y-2 text-sm">
              <div>
                {t("operatorObligationsPanel.oneTimeCost")}: <b>{formatEUR(estimate.oneTimeCost)}</b>
              </div>
              <div>
                {t("operatorObligationsPanel.annualCost")}: <b>{formatEUR(estimate.annualCost)}</b>
              </div>
              <div>
                {t("operatorObligationsPanel.inspectionsPerYear")}: <b>{Math.round(estimate.inspectionsPerYear)}</b>
              </div>
              <div className="text-muted-foreground">
                {estimate.assetsMatched} {t("operatorObligationsPanel.matchedAssets")} · {estimate.sitesCovered}{" "}
                {t("operatorObligationsPanel.sites")}
              </div>
            </div>
            {/* by role */}
            <div className="mt-4 min-w-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("operatorResponsibilityPanel.roleLabel")}</TableHead>
                    <TableHead className="text-right">{t("operatorObligationsPanel.inspectionsPerYear")}</TableHead>
                    <TableHead className="text-right">{t("operatorObligationsPanel.oneTimeCost")}</TableHead>
                    <TableHead className="text-right">{t("operatorObligationsPanel.annualCost")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(estimate.byRole || []).map((row: any) => (
                    <TableRow key={row.role}>
                      <TableCell>{qualificationRoleName(t, row.role)}</TableCell>
                      <TableCell className="text-right tabular-nums">{Math.round(row.inspectionsPerYear)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatEUR(row.oneTimeCost)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatEUR(row.annualCost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {estimate.flags?.unconfiguredRoles?.length ? (
              <p className="mt-2 text-xs text-destructive">
                {t("operatorObligationsPanel.unconfiguredRoles", {
                  roles: estimate.flags.unconfiguredRoles.map((r: string) => qualificationRoleName(t, r)).join(", "),
                })}
              </p>
            ) : null}
            {estimate.flags?.parameterDependentUnresolved ? (
              <p className="mt-2 text-xs text-warning">
                {t("operatorObligationsPanel.parameterDependentFlag", { count: estimate.flags.parameterDependentUnresolved })}
              </p>
            ) : null}

            <div className="mt-2 flex flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 max-sm:w-full"
                aria-expanded={isOpen}
                onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
              >
                {isOpen
                  ? <ChevronUp aria-hidden className="h-4 w-4" />
                  : <ChevronDown aria-hidden className="h-4 w-4" />}
                {t("operatorObligationsPanel.clusterDrilldown")}
              </Button>
            </div>
            {isOpen ? (
              <div className="mt-2 min-w-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("operatorObligationsPanel.site")}</TableHead>
                      <TableHead>{t("operatorResponsibilityPanel.roleLabel")}</TableHead>
                      <TableHead>{t("operatorObligationsPanel.period")}</TableHead>
                      <TableHead className="text-right">{t("operatorObligationsPanel.matchedAssets")}</TableHead>
                      <TableHead className="text-right">{t("operatorObligationsPanel.callOut")}</TableHead>
                      <TableHead className="text-right">{t("operatorObligationsPanel.perVisit")}</TableHead>
                      <TableHead className="text-right">{t("operatorObligationsPanel.cost")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(estimate.clusters || []).map((cluster: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="min-w-0 break-words">
                          {siteName(cluster.siteId)}
                          {cluster.bundesland ? ` (${cluster.bundesland})` : ""}
                        </TableCell>
                        <TableCell className="min-w-0 break-words">{qualificationRoleName(t, cluster.role)}</TableCell>
                        <TableCell className="min-w-0 break-words">
                          {cluster.recurring
                            ? t("operatorResponsibilityPanel.recurrenceMonths", { months: cluster.intervalMonths })
                            : t("operatorObligationsPanel.oneTime")}
                          {cluster.richtwert ? ` · ${t("operatorObligationsPanel.richtwert")}` : ""}
                          {cluster.parameterDependent ? ` · ${t("operatorObligationsPanel.parameterDependent")}` : ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{cluster.assetCount}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatEUR(cluster.callOutFee)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatEUR(cluster.perVisitCost)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cluster.recurring ? formatEUR(cluster.annualCost) : formatEUR(cluster.oneTimeCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </Section>
        );
      })}
    </div>
  );
}
